import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { xtreamLogin, type XtreamCredentials } from '../api/xtreamClient';
import type { XtreamAuthResponse, XtreamServerInfo, XtreamUserInfo } from '../types/xtream';

const STORAGE_USER = 'xtream_user';
const STORAGE_PASS = 'xtream_pass';

function readStoredCreds(): XtreamCredentials | null {
  const username = sessionStorage.getItem(STORAGE_USER)?.trim();
  const password = sessionStorage.getItem(STORAGE_PASS) ?? '';
  if (!username) return null;
  return { username, password };
}

interface PlayerSessionContextValue {
  creds: XtreamCredentials | null;
  userInfo: XtreamUserInfo | null;
  serverInfo: XtreamServerInfo | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  restoreSession: () => Promise<boolean>;
}

const PlayerSessionContext = createContext<PlayerSessionContextValue | null>(null);

export function PlayerSessionProvider({ children }: { children: ReactNode }) {
  const [creds, setCreds] = useState<XtreamCredentials | null>(() => readStoredCreds());
  const [userInfo, setUserInfo] = useState<XtreamUserInfo | null>(null);
  const [serverInfo, setServerInfo] = useState<XtreamServerInfo | null>(null);

  const persist = useCallback((c: XtreamCredentials | null) => {
    if (c) {
      sessionStorage.setItem(STORAGE_USER, c.username);
      sessionStorage.setItem(STORAGE_PASS, c.password);
    } else {
      sessionStorage.removeItem(STORAGE_USER);
      sessionStorage.removeItem(STORAGE_PASS);
    }
    setCreds(c);
  }, []);

  const applyAuth = useCallback((c: XtreamCredentials, auth: XtreamAuthResponse) => {
    persist(c);
    setUserInfo(auth.user_info ?? null);
    setServerInfo(auth.server_info ?? null);
  }, [persist]);

  const login = useCallback(async (username: string, password: string) => {
    const trimmedUser = username.trim();
    if (!trimmedUser || !password) {
      throw new Error('Podaj login i hasło');
    }
    const c = { username: trimmedUser, password };
    const auth = await xtreamLogin(c);
    applyAuth(c, auth);
  }, [applyAuth]);

  const logout = useCallback(() => {
    persist(null);
    setUserInfo(null);
    setServerInfo(null);
  }, [persist]);

  const restoreSession = useCallback(async () => {
    const stored = readStoredCreds();
    if (!stored) return false;
    try {
      const auth = await xtreamLogin(stored);
      applyAuth(stored, auth);
      return true;
    } catch {
      persist(null);
      setUserInfo(null);
      setServerInfo(null);
      return false;
    }
  }, [applyAuth, persist]);

  const value = useMemo<PlayerSessionContextValue>(
    () => ({
      creds,
      userInfo,
      serverInfo,
      isAuthenticated: !!creds && !!userInfo,
      login,
      logout,
      restoreSession,
    }),
    [creds, userInfo, serverInfo, login, logout, restoreSession],
  );

  return (
    <PlayerSessionContext.Provider value={value}>{children}</PlayerSessionContext.Provider>
  );
}

export function usePlayerSession(): PlayerSessionContextValue {
  const ctx = useContext(PlayerSessionContext);
  if (!ctx) throw new Error('usePlayerSession must be used within PlayerSessionProvider');
  return ctx;
}
